<template>
	<tr
		class="hover:bg-gray-300 cursor-pointer border-t"
		:class="{ 'bg-white opacity-75': notification.isRead, 'bg-gray-200': !notification.isRead }"
		@click="redirectToTarget"
	>
		<td class="px-6 py-4">
			<div class="flex items-center">
				<div class="mr-4 w-8 flex justify-center">
					<font-awesome-icon :icon="['fas', getIcon]" size="2x" />
				</div>
				<font-awesome-icon
					:icon="['fas', 'exclamation']"
					class="text-red-300 ml-4"
					size="2x"
					v-if="!notification.isRead && notification.type === 'CreateDamageCaseNotification'"
				/>
				<div class="ml-4 text-left">
					<div class="text-md leading-5 font-medium text-gray-900 font-bold">{{ getTitle }}</div>
					<div class="text-md leading-5 text-gray-500">
						{{ getIMDNet }}
					</div>
				</div>
			</div>
		</td>

		<td class="px-6 py-4 text-left whitespace-no-wrap">
			<div class="text-md leading-5 text-gray-900">{{ getDateString }}</div>
			<div class="text-md leading-5 text-gray-500">{{ getTimeString }}</div>
		</td>

		<td class="px-6 py-4 text-left hidden lg:block" v-if="!isUserAllowedInLevel1">
			<div class="text-md leading-5 text-gray-900">
				<div class="inline-block">{{ getVehicle.manufacturer }} {{ getVehicle.model }}</div>
			</div>
			<div class="text-md leading-5 text-gray-500">
				<div class="inline-block">
					{{ getOwnerName }}
				</div>
			</div>
		</td>

		<td class="px-6 py-4 text-left whitespace-no-wrap" v-if="isUserAllowedInLevel1">
			<div class="text-md leading-5 text-gray-900" v-if="notification.vehicle">
				{{ getVehicleModel(notification.vehicle) }}
			</div>
			<div class="text-md leading-5 text-gray-900" v-if="notification.manufacturer">
				{{ getVehicleManufacturer(notification.manufacturer) }}
			</div>
			<div class="text-md leading-5 text-gray-900" v-if="notification.rentalCar">
				{{ notification.rentalCar.manufacturer }} {{ notification.rentalCar.model }}
			</div>
			<div class="text-md leading-5 text-gray-500" v-if="notification.owner">{{ notification.owner.name }}</div>
		</td>

		<td class="px-6 py-4 text-left whitespace-no-wrap">
			<div class="text-md leading-5 text-gray-900">{{ getAuthorCompany }}</div>
			<div class="text-md leading-5 text-gray-500">{{ getAuthorName }}</div>
		</td>

		<td class="px-6 py-4 whitespace-no-wrap text-right text-lg font-medium">
			<font-awesome-icon
				:icon="['far', notification.isRead ? 'envelope-open' : 'envelope']"
				class="mr-4 cursor-pointer hover:text-xl text-xl font-regular"
				@click.stop="readNotification()"
				:title="notification.isRead ? $t('mark_unread').toString() : $t('mark_read').toString()"
			/>
			<font-awesome-icon :icon="['fas', 'trash-alt']" class="mr-4 cursor-pointer z" @click.stop="deleteNotification" title="Löschen" />
			<font-awesome-icon :icon="['fas', 'chevron-right']" class="cursor-pointer" @click="redirectToTarget" />
		</td>
		<router-view />
	</tr>
</template>
<script>
import { concatArray, getStringFromDate, getTimeFromDate } from '@/plugins/Utils';
import { NotificationApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import Authentication from '@/mixins/Authentication';
import { PDFTypes } from '@/api/generated/models';

export default {
	name: 'NotificationRow',
	mixins: [Authentication],
	props: {
		notification: {},
	},
	data() {
		return {
			meta: {
				CreateDamageCaseNotification: {
					url: 'damagecases.edit',
					params: { id: this.notification.damageCase?.id },
					title: this.$t('damage_case_stored'),
					icon: 'car-crash',
				},
				DistributeDamageCaseToDamageAssessorNotification: {
					url: 'damagecases.edit',
					params: { id: this.notification.damageCase?.id },
					title: this.$t('assigned_by_admin'),
					icon: 'car-crash',
				},
				UpdateDamageCaseRepairShopNotification: {
					url: 'damagecases.edit',
					params: { id: this.notification.damageCase?.id },
					title: this.$t('damage_case_stored'),
					icon: 'car-crash',
				},
				CreateCalculationPreNotification: {
					url: 'damagecases.edit',
					params: { id: this.notification.damageCase?.id },
					title: this.$t('repair_allowed_calc_raw'),
					icon: 'stamp',
				},
				CreateRentalCarContractNotification: {
					url: 'damagecases.edit',
					params: { id: this.notification.damageCase?.id },
					title: this.$t('rental_car_saved'),
					icon: 'taxi',
				},
				CreateWorkingImageNotification: {
					url: 'damagecases.edit.workingImages',
					params: { id: this.notification.damageCase?.id },
					title: this.$t('working_photo_stored'),
					icon: 'image',
				},
				CreateDokumentNotification: {
					url: 'damagecases.edit',
					params: { id: this.notification.damageCase?.id },
					title: this.$t('document_stored', { documentName: this.notification.document?.label }),
					icon: 'file',
				},
				CreateDocumentL3GutachtenNotification: {
					url: 'damagecases.edit',
					params: { id: this.notification.damageCase?.id },
					title: this.$t('document_stored', { documentName: this.notification.document?.label }),
					icon: 'file',
				},
				CreateRepairExtensionNotification: {
					url: 'damagecases.edit.repairExtensions',
					params: { processId: this.notification.damageCase?.id, customer: this.notification.damageCase?.customer.id },
					title: this.$t('applied_repair_extension'),
					icon: 'tools',
				},
				CreateRepairExtensionCustomerConsultantNotification: {
					url: 'damagecases.edit.repairExtensions',
					params: { processId: this.notification.damageCase?.id, customer: this.notification.damageCase?.customer.id },
					title: this.$t('applied_repair_extension_work_shop'),
					icon: 'tools',
				},
				CreateRepairExtensionCalculationUploadNotification: {
					url: 'damagecases.edit.repairExtensions',
					params: { processId: this.notification.damageCase?.id, customer: this.notification.damageCase?.customer.id },
					title: this.$t('repair_extension_stored'),
					icon: 'tools',
				},
				UpdateStatusRepairExtensionNotification: {
					url: 'damagecases.edit.repairExtensions',
					params: { processId: this.notification.damageCase?.id, customer: this.notification.damageCase?.customer.id },
					title: this.$t('allowed_repair_extension', {
						status:
							this.notification.misc?.status === 'rollback'
								? this.$t('rollback')
								: this.notification.misc?.status === 'approved'
								? this.$t('received')
								: this.$t('denied'),
					}),
					icon: 'tools',
				},
				CreateRentalCarNotification: {
					url: 'RentalCarEdit',
					params: { vehicleId: this.notification.rentalCar?.id },
					title: this.$t('new_rental_car'),
					icon: 'taxi',
				},
				CreateNewVehicleManufacturerOrModelNotification: {
					url: 'vehicles.model.id',
					title: this.$t('new_model_stored'),
					icon: 'car',
					params: { id: this.notification.vehicle?.id },
				},
				CreateVehicleManufacturerNotification: {
					url: 'vehicles.manufacturer.id',
					title: this.$t('new_manufacturer_stored'),
					icon: 'industry',
					params: { id: this.notification.manufacturer?.id },
				},
				CreateRentalCarClassificationNotification: {
					url: 'rentalcars',
					title: this.$t('new_rental_car_classified'),
					icon: 'taxi',
				},
				UpdateDamageAssessorPdfPricingNotification: {
					url: 'damageassessor.editpdfpricing',
					params: {},
					title: this.$t('fee_plan_changed'),
					icon: 'money-bill',
				},
				CreateUpdateDatPricesNotification: {
					url: 'rentalcar.pricing',
					title: this.$t('dat_pricing_updated'),
					icon: 'euro-sign',
				},
				CreateOppositeInsuranceInDamageCase: {
					url: 'insurancesprofile',
					title: this.$t('new_insurance_data_pending'),
					icon: 'shield-alt',
					params: { id: this.notification.insurance?.id },
				},
				ApplyDamageAssessorNotification: {
					url: this.notification.damageAssessor?.damageAssessorCompany?.id ? 'OtherCompanyProfile' : 'administrate_new_companies',
					title:
						this.$t('new_assessor_applied') + ' ' + (this.notification.damageAssessor?.damageAssessorCompany?.id ? this.$t('already_created') : ''),
					icon: 'user-secret',
					params: {
						id: this.notification.damageAssessor?.damageAssessorCompany?.company.id,
						payload: this.notification.damageAssessor,
						type: 'damageassessor',
					},
				},
				ApplyLawyerOfficeNotification: {
					url: this.notification.lawyerOffice?.lawyerOffice?.id ? 'OtherCompanyProfile' : 'administrate_new_companies',
					title: this.$t('new_lawyer_applied') + ' ' + (this.notification.lawyerOffice?.lawyerOffice?.id ? this.$t('already_created') : ''),
					icon: 'gavel',
					params: {
						id: this.notification.lawyerOffice?.lawyerOffice?.company.id,
						payload: this.notification.lawyerOffice,
						type: 'lawyerOffice',
					},
				},
				AssignDamageAssessorToRepairShopNotification: {
					url: 'repair_shop.service_provider',
					title: this.$t('assigned_assessors_changed'),
					icon: 'user-secret',
				},
				AssignLawyerOfficeToRepairShopNotification: {
					url: 'repair_shop.service_provider',
					title: this.$t('assigned_lawyers_changed'),
					icon: 'gavel',
				},
				VehicleDeactivatedByCustomerNotification: {
					url: 'management.customer.vehicles.edit',
					title: this.$t('customer_marked_vehicle_deactivated'),
					icon: 'car',
					params: { customer: this.notification.vehicle?.ownable_id, vehicleId: this.notification.vehicle?.id },
				},
				SignDocumentsNotification: {
					url: 'management.process.edit',
					params: {
						processId: this.notification.damageCase?.id,
						customer: this.notification.damageCase?.customer.id,
						scrollToDocs: 'sign_documents',
					},
					title: this.$t('customer_signed_document', { type: this.notification.document }),
					icon: 'signature',
				},
				UpdateCompanyLogoNotification: {
					url: 'OtherCompanyProfile',
					title: this.getLogoTitle(this.notification),
					icon: 'image',
					params: {
						id: this.notification.company?.id,
						type:
							this.notification.author != null && this.notification.author.usergroup.level === 5
								? 'car_dealership'
								: this.notification.author != null && this.notification.author.usergroup.level === 6
								? 'repairShop'
								: this.notification.author != null && this.notification.author.usergroup.level === 2
								? 'damageassessor'
								: 'lawyerOffice',
						backlink: {
							name: 'administrate_companies',
							params: {
								type:
									this.notification.author != null && this.notification.author.usergroup.level === 5
										? 'car_dealership'
										: this.notification.author != null && this.notification.author.usergroup.level === 6
										? 'repairShop'
										: this.notification.author != null && this.notification.author.usergroup.level === 2
										? 'damageassessor'
										: 'lawyerOffice',
							},
						},
					},
				},
				UpdateCompanyLetterHeaderNotification: {
					url: 'OtherCompanyProfile',
					title: this.getLetterHeaderTitle(this.notification),
					icon: 'image',
					params: {
						id: this.notification.company?.id,
						type:
							this.notification.author != null && this.notification.author.usergroup.level === 5
								? 'car_dealership'
								: this.notification.author != null && this.notification.author.usergroup.level === 6
								? 'repairShop'
								: this.notification.author != null && this.notification.author.usergroup.level === 2
								? 'damageassessor'
								: 'lawyerOffice',
						backlink: {
							name: 'administrate_companies',
							params: {
								type:
									this.notification.author != null && this.notification.author.usergroup.level === 5
										? 'car_dealership'
										: this.notification.author != null && this.notification.author.usergroup.level === 6
										? 'repairShop'
										: this.notification.author != null && this.notification.author.usergroup.level === 2
										? 'damageassessor'
										: 'lawyerOffice',
							},
						},
					},
				},
				OnboardingCompletedNotification: {
					url: 'OtherCompanyProfile',
					title: this.getOnboardingHeaderTitle(this.notification),
					icon: 'check-circle',
					params: {
						id: this.notification.company?.id,
						type:
							this.notification.author != null && this.notification.author.usergroup.level === 5
								? 'car_dealership'
								: this.notification.author != null && this.notification.author.usergroup.level === 6
								? 'repairShop'
								: this.notification.author != null && this.notification.author.usergroup.level === 2
								? 'damageassessor'
								: 'lawyerOffice',
						backlink: {
							name: 'administrate_companies',
							params: {
								type:
									this.notification.author != null && this.notification.author.usergroup.level === 5
										? 'car_dealership'
										: this.notification.author != null && this.notification.author.usergroup.level === 6
										? 'repairShop'
										: this.notification.author != null && this.notification.author.usergroup.level === 2
										? 'damageassessor'
										: 'lawyerOffice',
							},
						},
					},
				},
				CheckCompanySignNotification: {
					url: 'OtherCompanyProfile',
					title: this.$t('damage_assessor_company_edited_sign'),
					icon: 'signature',
					params: {
						id: this.notification.company?.id,
						type: 'damageassessor',
						backlink: {
							name: 'administrate_companies',
							params: { type: 'damageassessor' },
						},
					},
				},
				UnblockAssetNotification: {
					url: 'CompanyProfile',
					title: this.$t('unblocked_asset', {
						type: this.notification.assetType === 'logo' ? 'Ihr Logo' : this.notification.assetType === 'sign' ? 'Ihre Signatur' : 'Ihr Briefkopf',
					}),
					icon: 'image',
				},
				EndMandateEarlyNotification: {
					url: 'management.process.edit',
					params: {
						processId: this.notification.damageCase?.id,
						customer: this.notification.damageCase?.customer.id,
					},
					title: this.$t('lawyer_mandate_ended_early'),
					icon: 'handshake-slash',
				},
				UpdateDamageCasePaymentReceiptsNotification: {
					url: 'damagecases.payment',
					params: { id: this.notification.damageCase?.id },
					title: this.$t('new_payment_receipt_added_by_assessor'),
					icon: 'money-bill',
				},
				CreateBrokerCaseNotification: {
					url: 'brokerCaseDetail',
					params: { id: this.notification.brokerCase?.id },
					title: this.$t('new_broker_case_saved'),
					icon: 'user-tie',
				},
			},
		};
	},
	mounted() {
		this.meta.CreateDamageCaseNotification.title += this.getLawyerTitle();
		this.setDamageCaseCreatedTitle();
	},
	methods: {
		getVehicleModel(vehicle) {
			if (vehicle.wmi) {
				return `${vehicle.wmi} ${vehicle.vds} - ${vehicle.name}`;
			} else {
				return vehicle.name;
			}
		},
		getVehicleManufacturer(vehicle) {
			if (vehicle.wmi) {
				return `${vehicle.wmi} - ${vehicle.name}`;
			} else {
				return vehicle.name;
			}
		},
		getLetterHeaderTitle(notification) {
			if (notification.author != null) {
				if (notification.author.usergroup.level === 5) {
					return this.$t('company_edited_letter_header', { companyType: 'Ein Autohaus' });
				} else if (notification.author.usergroup.level === 6) {
					return this.$t('company_edited_letter_header', { companyType: 'Ein Reparaturbetrieb' });
				} else if (notification.author.usergroup.level === 2) {
					return this.$t('company_edited_letter_header', { companyType: 'Ein Gutachter' });
				} else {
					return this.$t('lawyer_edited_letter_header');
				}
			}
		},
		getLogoTitle(notification) {
			if (notification.author != null) {
				if (notification.author.usergroup.level === 5) {
					return this.$t('company_edited_logo', { companyType: 'Ein Autohaus' });
				} else if (notification.author.usergroup.level === 6) {
					return this.$t('company_edited_logo', { companyType: 'Ein Reparaturbetrieb' });
				} else if (notification.author.usergroup.level === 2) {
					return this.$t('company_edited_logo', { companyType: 'Ein Gutachter' });
				} else {
					return this.$t('lawyer_edited_logo');
				}
			}
		},
		getOnboardingHeaderTitle(notification) {
			if (notification.author != null) {
				if (notification.author.usergroup.level === 5) {
					return this.$t('company_finished_onboarding', { companyType: 'Ein Autohaus' });
				} else if (notification.author.usergroup.level === 6) {
					return this.$t('company_finished_onboarding', { companyType: 'Ein Reparaturbetrieb' });
				} else if (notification.author.usergroup.level === 2) {
					return this.$t('company_finished_onboarding', { companyType: 'Ein Gutachter' });
				} else {
					return this.$t('lawyer_finished_onboarding');
				}
			}
		},
		setDamageCaseCreatedTitle() {
			let title;
			const customerConsultant = this.getCustomerConsultant;
			const authorLevel = this.notification.author?.usergroup?.level;
			const authorIsNotLevel5or6 = authorLevel !== 6 && authorLevel !== 5;

			if (this.isUserAllowedInLevel5or6 && authorIsNotLevel5or6) {
				title = this.$t('damage_case_transferred');
				if (customerConsultant) {
					title += ' (verwaltet von ' + customerConsultant.firstName + ' ' + customerConsultant.lastName + ')';
				}
			} else if (this.isUserAllowedInLevel2) {
				title = this.$t('damage_case_stored') + this.$t('assign_damage_assessor');
			} else {
				title = this.$t('damage_case_stored');
			}

			this.meta.CreateDamageCaseNotification.title = title;
		},
		redirectToTarget($event, url, params) {
			this.readNotification(true).then(result => {
				this.$router.push({ name: url ?? this.getURL, params: params ?? this.getParams });
			});
		},
		readNotification(onlySetRead = false) {
			if (onlySetRead) {
				this.notification.isRead = true;
			} else {
				this.notification.isRead = !this.notification.isRead; // read/unread notifications
			}
			return NotificationApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.notificationIdPut(this.notification.id, { isRead: this.notification.isRead })
				.then(response => {
					this.$store.dispatch('updateNotifications');
					this.$store.dispatch('updateNotificationCount');
				});
		},
		deleteNotification() {
			this.$swal({
				title: this.$t('sure').toString(),
				text: this.$t('delete_entry').toString(),
				icon: 'warning',
				cancelButtonText: this.$t('general.no').toString(),
				showCancelButton: true,
				confirmButtonText: this.$t('general.yes').toString(),
			}).then(result => {
				if (result.value) {
					return NotificationApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
						.notificationIdDelete(this.notification.id)
						.then(response => {
							this.$store.dispatch('updateNotifications');
						});
				}
			});
		},
		getDocumentType() {
			switch (this.notification?.document?.type) {
				case PDFTypes.Workingimages:
					return 'Arbeitsfotos';
			}
			return '';
		},
		getLawyerTitle() {
			//if (this.getAuthenticatedUserGroupLevel === '3' && !this.notification?.damageCase?.oppositeInsurance) {
			if (this.getAuthenticatedUserGroupLevel === '3') {
				return this.$t('send_mandate_form');
			}

			return '';
		},
	},
	computed: {
		getCustomerConsultant() {
			return this.notification.damageCase?.customerConsultant;
		},
		getAuthorName() {
			return this.notification.author != null
				? concatArray([this.notification.author?.person?.firstName, this.notification.author?.person?.lastName])
				: '';
		},
		getAuthorCompany() {
			return this.notification.author != null ? this.notification.author?.person?.company?.name : '';
		},
		getAuthorLevel() {
			return this.notification.author != null ? this.notification.author?.usergroup?.name : '';
		},
		getVehicle() {
			if (this.notification.type === 'VehicleDeactivatedByCustomerNotification') {
				return this.notification.vehicle;
			}
			return this.notification.damageCase?.vehicle || {};
		},
		getIMDNet() {
			return this.notification?.damageCase?.imdNetId;
		},
		getURL() {
			return this.meta[this.notification.type].url;
		},
		getParams() {
			return this.meta[this.notification.type].params;
		},
		getDate() {
			return new Date(this.notification.created_at * 1000);
		},
		getDateString() {
			return getStringFromDate(this.getDate);
		},
		getTimeString() {
			return getTimeFromDate(this.getDate);
		},
		getTitle() {
			return this.meta[this.notification.type]?.title;
		},
		getIcon() {
			return this.meta[this.notification.type]?.icon;
		},
		getOwnerName() {
			if (this.notification.type === 'VehicleDeactivatedByCustomerNotification') {
				return this.notification.vehicle.owner_name;
			}
			if (this.notification.damageCase?.customer.gender === 'company') {
				let companyName = this.notification.damageCase?.customer.company?.name;
				if (companyName.length > 25) {
					companyName = companyName.slice(0, 25) + '...';
				}
				return companyName;
			}
			return concatArray([this.notification.damageCase?.customer?.firstName, this.notification.damageCase?.customer?.lastName]);
		},
	},
};
</script>
